/* eslint-disable react/prop-types */
import React, { useCallback } from 'react';

import cn from 'classnames';
import { noop } from 'lodash';
import {
  MdCancel,
  MdCheckCircle,
  MdDoNotDisturb,
  MdHelp,
} from 'react-icons/md';
import { FormattedMessage } from 'react-intl';
import { Text } from 'scouting-ui/lib';

import { rsvpCodes, rsvpKey } from '../../constants';
import { Collapse } from '../bsaComponents';
import PersonList from './PersonList';
import styles from './PersonListRSVP.less';
import { PersonData, PersonsRSVPList } from './types';

const { Panel } = Collapse;

interface PersonListRSVPProps {
  persons: PersonsRSVPList;
  onAttendedToggle: (person: PersonData, status: boolean) => void;
  isAttendedToggleLoading: boolean;
  onRemoveAction: (data: PersonData) => void;
  onRSVPAction: (data: PersonData, code: typeof rsvpCodes) => void;
  innerExpandedKeys: string[];
  handleInnerPanelChange: (keys: string[]) => void;
  expandedKeys: string | string[];
  onPanelChange: (keys: string | string[]) => void;
  recurringEventId: number | null;
  isCreateEvent: boolean;
  enableAttendance: boolean;
  hasPackOrDen: boolean;
  myYouthsInEvent?: { [key: string]: string };
  userOrgsMemberIds?: Set<number> | null;
}

const PersonListRSVP = React.memo<PersonListRSVPProps>(
  ({
    recurringEventId,
    persons,
    onAttendedToggle = noop,
    isAttendedToggleLoading = false,
    onRemoveAction = noop,
    onRSVPAction = noop,
    expandedKeys,
    onPanelChange,
    innerExpandedKeys,
    handleInnerPanelChange,
    isCreateEvent,
    enableAttendance,
    hasPackOrDen = false,
    myYouthsInEvent = {},
    userOrgsMemberIds = null,
  }) => {
    const { attending, notAttending, maybe, noResponse } = persons;

    const personsCount = useCallback(
      persons => {
        const denChiefCount = hasPackOrDen ? persons.denChiefs.length : 0;

        return (
          persons.youths.length +
          persons.adults.length +
          persons.parents.length +
          persons.guests.length +
          denChiefCount
        );
      },
      [hasPackOrDen],
    );

    const collapseDetails = useCallback(
      (
        attendingPersons,
        notAttendingPersons,
        maybePersons,
        noResponsePersons,
      ) => (
        <Collapse
          bordered={false}
          activeKey={expandedKeys}
          onChange={keys => onPanelChange(keys)}
        >
          <Panel
            header={
              <Text>
                <MdCheckCircle className={styles.rsvpYesIcon} />
                <FormattedMessage id={rsvpKey.YES} /> (
                {personsCount(attendingPersons)})
              </Text>
            }
            key={rsvpKey.YES}
            className={cn(styles.panel, styles.noPadding)}
          >
            <PersonList
              persons={attendingPersons}
              rsvpKey={rsvpKey.YES}
              onAttendedToggle={onAttendedToggle}
              isAttendedToggleLoading={isAttendedToggleLoading}
              onRemoveAction={onRemoveAction}
              onRSVPAction={onRSVPAction}
              innerExpandedKeys={innerExpandedKeys}
              handleInnerPanelChange={handleInnerPanelChange}
              isRSVP={true}
              isCreateEvent={isCreateEvent}
              enableAttendance={enableAttendance}
              hasPackOrDen={hasPackOrDen}
              myYouthsInEvent={myYouthsInEvent}
              userOrgsMemberIds={userOrgsMemberIds}
            />
          </Panel>
          <Panel
            header={
              <Text>
                <MdCancel className={styles.rsvpNoIcon} />
                <FormattedMessage id={rsvpKey.NO} /> (
                {personsCount(notAttendingPersons)})
              </Text>
            }
            key={rsvpKey.NO}
            className={cn(styles.panel, styles.noPadding)}
          >
            <PersonList
              persons={notAttendingPersons}
              rsvpKey={rsvpKey.NO}
              onAttendedToggle={onAttendedToggle}
              isAttendedToggleLoading={isAttendedToggleLoading}
              onRemoveAction={onRemoveAction}
              onRSVPAction={onRSVPAction}
              innerExpandedKeys={innerExpandedKeys}
              handleInnerPanelChange={handleInnerPanelChange}
              isRSVP={true}
              recurringEventId={recurringEventId}
              isCreateEvent={isCreateEvent}
              enableAttendance={enableAttendance}
              hasPackOrDen={hasPackOrDen}
              myYouthsInEvent={myYouthsInEvent}
              userOrgsMemberIds={userOrgsMemberIds}
            />
          </Panel>
          <Panel
            header={
              <Text>
                <MdHelp className={styles.rsvpMaybeIcon} />
                <FormattedMessage id={rsvpKey.MAYBE} /> (
                {personsCount(maybePersons)})
              </Text>
            }
            key={rsvpKey.MAYBE}
            className={cn(styles.panel, styles.noPadding)}
          >
            <PersonList
              persons={maybePersons}
              rsvpKey={rsvpKey.MAYBE}
              onAttendedToggle={onAttendedToggle}
              isAttendedToggleLoading={isAttendedToggleLoading}
              onRemoveAction={onRemoveAction}
              onRSVPAction={onRSVPAction}
              innerExpandedKeys={innerExpandedKeys}
              handleInnerPanelChange={handleInnerPanelChange}
              isRSVP={true}
              recurringEventId={recurringEventId}
              isCreateEvent={isCreateEvent}
              enableAttendance={enableAttendance}
              hasPackOrDen={hasPackOrDen}
              myYouthsInEvent={myYouthsInEvent}
              userOrgsMemberIds={userOrgsMemberIds}
            />
          </Panel>
          <Panel
            header={
              <Text>
                <MdDoNotDisturb className={styles.rsvpNoResponseIcon} />
                <FormattedMessage id={rsvpKey.NO_RESPONSE} /> (
                {personsCount(noResponse)})
              </Text>
            }
            key={rsvpKey.NO_RESPONSE}
            className={cn(styles.panel, styles.noPadding)}
          >
            <PersonList
              persons={noResponsePersons}
              rsvpKey={rsvpKey.NO_RESPONSE}
              onAttendedToggle={onAttendedToggle}
              isAttendedToggleLoading={isAttendedToggleLoading}
              onRemoveAction={onRemoveAction}
              onRSVPAction={onRSVPAction}
              innerExpandedKeys={innerExpandedKeys}
              handleInnerPanelChange={handleInnerPanelChange}
              isRSVP={true}
              recurringEventId={recurringEventId}
              isCreateEvent={isCreateEvent}
              enableAttendance={enableAttendance}
              hasPackOrDen={hasPackOrDen}
              myYouthsInEvent={myYouthsInEvent}
              userOrgsMemberIds={userOrgsMemberIds}
            />
          </Panel>
        </Collapse>
      ),
      [
        expandedKeys,
        personsCount,
        onAttendedToggle,
        isAttendedToggleLoading,
        onRemoveAction,
        onRSVPAction,
        innerExpandedKeys,
        handleInnerPanelChange,
        isCreateEvent,
        enableAttendance,
        hasPackOrDen,
        myYouthsInEvent,
        recurringEventId,
        noResponse,
        onPanelChange,
        userOrgsMemberIds,
      ],
    );

    return (
      <div>{collapseDetails(attending, notAttending, maybe, noResponse)}</div>
    );
  },
);

PersonListRSVP.displayName = 'PersonListRSVP';

export default PersonListRSVP;
